import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import api from 'src/services/axiosConfig';
import { useAuthContext } from 'src/contexts/auth-context';

const DEBUG_TRACE_KEY = 'sorby:agentDebugTrace';

const initialState = {
  messages: [],
  isLoadingHistory: false,
  isSending: false,
  sessionActive: false,
  awaitingConfirm: false,
  confirmAction: null,
  activeSpecialist: null,
  reportDraft: null,
  suggestions: [],
  error: null,
  hasLoadedHistory: false,
};

// El specialist de reportes no persiste activeSpecialist (queda null), así que el draft
// se mantiene mientras llegue uno nuevo y se limpia solo si el turno fue claramente de
// OTRO specialist (activeSpecialist no nulo y distinto de 'reportes').
function resolveReportDraft(state, payload) {
  if (payload.activeSpecialist && payload.activeSpecialist !== 'reportes') return null;
  return payload.reportDraft ?? state.reportDraft;
}

function reducer(state, action) {
  switch (action.type) {
    case 'load:start':
      return { ...state, isLoadingHistory: true, error: null };
    case 'load:success':
      return {
        ...state,
        isLoadingHistory: false,
        hasLoadedHistory: true,
        messages: action.payload.messages,
        sessionActive: action.payload.sessionActive,
        awaitingConfirm: action.payload.awaitingConfirm,
        confirmAction: action.payload.confirmAction,
        activeSpecialist: action.payload.activeSpecialist || null,
        reportDraft: resolveReportDraft(state, action.payload),
        suggestions: Array.isArray(action.payload.suggestions) ? action.payload.suggestions : [],
      };
    case 'load:error':
      // Marcamos hasLoadedHistory: true aunque haya fallado para evitar reintentos
      // automáticos en el useEffect (que dispararían loop infinito al cambiar la
      // referencia del callback). El reset() vuelve a poner el flag en false.
      return { ...state, isLoadingHistory: false, hasLoadedHistory: true, error: action.payload };
    case 'send:start':
      return {
        ...state,
        isSending: true,
        error: null,
        // Los chips del turno anterior ya se consumieron al enviar; se limpian.
        suggestions: [],
        messages: [...state.messages, action.payload.optimisticMessage],
      };
    case 'send:success':
      return {
        ...state,
        isSending: false,
        messages: [...state.messages, action.payload.assistantMessage],
        sessionActive: action.payload.sessionActive,
        awaitingConfirm: action.payload.awaitingConfirm,
        confirmAction: action.payload.confirmAction,
        activeSpecialist: action.payload.activeSpecialist,
        reportDraft: resolveReportDraft(state, action.payload),
        suggestions: Array.isArray(action.payload.suggestions) ? action.payload.suggestions : [],
        lastDebugTrace: action.payload.debugTrace || null,
      };
    case 'send:error':
      return {
        ...state,
        isSending: false,
        error: action.payload,
        // Mantenemos el mensaje optimista del user; solo no se agrega el del assistant
      };
    case 'editSession:start':
      return { ...state, isLoadingHistory: true, error: null };
    case 'editSession:success':
      // Arranque "editar reporte con agente": reemplaza el contexto por el saludo + draft.
      return {
        ...initialState,
        hasLoadedHistory: true,
        messages: [action.payload.assistantMessage],
        reportDraft: action.payload.reportDraft ?? null,
        suggestions: Array.isArray(action.payload.suggestions) ? action.payload.suggestions : [],
      };
    case 'reset':
      return { ...initialState, hasLoadedHistory: true };
    case 'error:dismiss':
      return { ...state, error: null };
    default:
      return state;
  }
}

function extractErrorMessage(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const backendMsg = typeof data?.error === 'string' ? data.error : null;
  if (status === 403) {
    return backendMsg || 'No tenés acceso al asistente.';
  }
  if (status === 401) {
    return backendMsg || 'Sesión expirada. Volvé a ingresar.';
  }
  if (backendMsg) return backendMsg;
  return 'Ocurrió un error. Probá de nuevo.';
}

/**
 * Encapsula el estado y las llamadas al backend del agente web. Una sola conversación
 * por usuario; el historial se carga la primera vez que se abre el chat y los mensajes
 * se acumulan localmente turno a turno (el backend ya los persiste).
 */
/**
 * `contexto` es de dónde se está preguntando: qué obra hay abierta en la pantalla.
 * Viaja en un ref y no en las dependencias de `sendMessage` para que el callback
 * siga siendo estable — si cambiara de identidad en cada render, el input se
 * remontaría y perdería el foco mientras alguien escribe.
 */
export function useAgentChat({ contexto = null } = {}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const inFlightRef = useRef(false);
  const { user } = useAuthContext();
  const userIdRef = useRef(null);
  userIdRef.current = user?.user_id || null;
  const contextoRef = useRef(null);
  contextoRef.current = contexto;

  const loadHistory = useCallback(async () => {
    if (state.hasLoadedHistory || state.isLoadingHistory) return;
    dispatch({ type: 'load:start' });
    try {
      const { data } = await api.get('/agent/conversation', { params: { user_id: userIdRef.current } });
      dispatch({ type: 'load:success', payload: data });
    } catch (err) {
      dispatch({ type: 'load:error', payload: extractErrorMessage(err) });
    }
  }, [state.hasLoadedHistory, state.isLoadingHistory]);

  const sendMessage = useCallback(async (text, files) => {
    const trimmed = (text || '').trim();
    const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
    // Permitimos enviar si hay texto O al menos un archivo adjunto.
    if ((!trimmed && fileList.length === 0) || inFlightRef.current) return;
    inFlightRef.current = true;
    // Los dos turnos del intercambio se marcan con la obra abierta, igual que los
    // persiste el backend: si no, al volver a leerlos no se sabe de qué obra eran.
    const obraDelTurno = contextoRef.current?.control_obra_id
      ? { control_obra_id: contextoRef.current.control_obra_id }
      : {};
    const optimisticMessage = {
      role: 'user',
      content: trimmed || '📎 Archivo adjunto',
      createdAt: new Date().toISOString(),
      ...obraDelTurno,
      ...(fileList.length ? { attachments: fileList.map((f) => ({ filename: f.name })) } : {}),
    };
    dispatch({ type: 'send:start', payload: { optimisticMessage } });
    try {
      let data;
      if (fileList.length > 0) {
        const form = new FormData();
        if (trimmed) form.append('message', trimmed);
        if (userIdRef.current) form.append('user_id', userIdRef.current);
        fileList.forEach((file) => form.append('archivos', file));
        if (contextoRef.current) form.append('contexto', JSON.stringify(contextoRef.current));
        // No seteamos Content-Type: el browser agrega el boundary del multipart.
        ({ data } = await api.post('/agent/chat', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }));
      } else {
        ({ data } = await api.post('/agent/chat', {
          message: trimmed,
          user_id: userIdRef.current,
          ...(contextoRef.current ? { contexto: contextoRef.current } : {}),
        }));
      }
      dispatch({
        type: 'send:success',
        payload: {
          assistantMessage: {
            role: 'assistant',
            content: data.replyText,
            createdAt: new Date().toISOString(),
            ...obraDelTurno,
            debugTrace: data.debugTrace || null,
            actions: Array.isArray(data.actions) ? data.actions : [],
          },
          sessionActive: data.sessionActive,
          awaitingConfirm: data.awaitingConfirm,
          confirmAction: data.confirmAction,
          activeSpecialist: data.activeSpecialist || null,
          reportDraft: data.reportDraft ?? null,
          suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
          debugTrace: data.debugTrace || null,
        },
      });
    } catch (err) {
      dispatch({ type: 'send:error', payload: extractErrorMessage(err) });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Arranca una sesión determinística de edición de un reporte existente: el backend limpia
  // el contexto, carga el reporte como preview y devuelve el saludo fijo. Reemplaza el estado.
  const startEditSession = useCallback(async (reportId) => {
    if (!reportId || inFlightRef.current) return;
    inFlightRef.current = true;
    dispatch({ type: 'editSession:start' });
    try {
      const { data } = await api.post(`/agent/report/${reportId}/edit-session`, { user_id: userIdRef.current });
      dispatch({
        type: 'editSession:success',
        payload: {
          assistantMessage: {
            role: 'assistant',
            content: data.replyText,
            createdAt: new Date().toISOString(),
            actions: Array.isArray(data.actions) ? data.actions : [],
          },
          reportDraft: data.reportDraft ?? null,
          suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
        },
      });
    } catch (err) {
      dispatch({ type: 'load:error', payload: extractErrorMessage(err) });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const reset = useCallback(async () => {
    try {
      await api.post('/agent/reset', { user_id: userIdRef.current });
      dispatch({ type: 'reset' });
    } catch (err) {
      dispatch({ type: 'load:error', payload: extractErrorMessage(err) });
    }
  }, []);

  const confirmCurrent = useCallback(() => sendMessage('sí'), [sendMessage]);
  const cancelCurrent = useCallback(() => sendMessage('cancelar'), [sendMessage]);
  const dismissError = useCallback(() => dispatch({ type: 'error:dismiss' }), []);

  return {
    ...state,
    loadHistory,
    sendMessage,
    startEditSession,
    reset,
    confirmCurrent,
    cancelCurrent,
    dismissError,
  };
}

/**
 * Toggle persistente para mostrar/ocultar el trace dev del agente.
 * Solo disponible si el backend incluye debugTrace en las respuestas (NODE_ENV !== production).
 */
export function useAgentDebugTrace() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(DEBUG_TRACE_KEY);
      if (raw === '1') setEnabled(true);
    } catch (_) { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(DEBUG_TRACE_KEY, next ? '1' : '0');
      } catch (_) { /* noop */ }
      return next;
    });
  }, []);

  return { enabled, toggle };
}
